import APIError from "./api.error";
import { IQuery, IBody, IHeaders, IXHR } from "./types";
import { buildQueryString } from "../querystring";

type RequestOptions = {
  headers?: IHeaders;
  query?: IQuery;
  disableFilter?: boolean;
  credentials?: boolean;
};

type RequestParams = RequestOptions & {
  url: string;
  method: "POST" | "GET" | "PUT" | "PATCH" | "DELETE";
  body?: IBody | FormData;
  disableFilter?: boolean;
  credentials?: boolean;
};

type XHROptions = RequestOptions & {
  onProgress?: (e: ProgressEvent) => any;
  onSuccess?: (result: any) => any;
  onFail?: (e?: Error) => any;
  onCancel?: () => any;
};

async function transformResponse(response: Response) {
  try {
    return await response.json();
  } catch (err) {
    return null;
  }
}

export default class APIClient {
  protected _baseUrl: string;
  private _defaultHeaders: IHeaders;
  public onUnauthorized?: () => void;

  /**
   * Called when a request comes back 401 and no retry has been attempted yet.
   *
   * Resolve true once a fresh token has been installed through setToken(); the
   * original request is then sent again with it. Resolve false to fall through
   * to onUnauthorized, which ends the session.
   *
   * This exists because the access token for https://ssgyms lives 2 hours and
   * nothing renews it while a tab sits open — checkSession() runs once on
   * mount. So an ordinary 401 usually means "the token aged out", not "this
   * person lost access", and treating the two the same threw coaches to the
   * email-code screen mid-task. Measured on production 2026-08-14: 18 of 18
   * logouts in a 47-hour window were preceded by a 401 about 0.3 s earlier,
   * hitting 23 of 39 active coaches, twice while saving a member's workout.
   */
  public onTokenExpired?: () => Promise<boolean>;

  constructor(baseUrl: string) {
    this._baseUrl = baseUrl;
    this._defaultHeaders = {
      "Content-Type": "application/json"
    };
    this.responseHandler = this.responseHandler.bind(this);
  }

  protected responseHandler(response: Response) {
    if (response.status >= 200 && response.status < 300) {
      return transformResponse(response);
    }

    if (response.status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
    }

    return transformResponse(response).then(data => {
      throw new APIError(data, response.status);
    });
  }

  protected buildUrl(url: string, query?: IQuery): string {
    let queryString = "";
    if (query) {
      queryString = buildQueryString(query);
    }

    if (url[0] === "/") {
      return `${this._baseUrl}${url}${queryString}`;
    }

    return `${this._baseUrl}/${url}${queryString}`;
  }

  protected async sendRequest(
    options: RequestParams,
    isRetry = false
  ): Promise<any> {
    // Headers are rebuilt from _defaultHeaders on every call, so a retry that
    // follows a successful renewal automatically carries the new token.
    let headers: IHeaders = {
      ...this._defaultHeaders
    };

    if (options.headers) {
      headers = {
        ...headers,
        ...options.headers
      };
    }

    let opts: RequestInit = {
      method: options.method,
      headers
    };

    if (options.credentials) {
      opts = {
        ...opts,
        credentials: "include"
      };
    }

    if (options.body) {
      if (options.body instanceof FormData) {
        delete headers["Content-Type"];
        opts = {
          ...opts,
          body: options.body
        };
      } else {
        opts = {
          ...opts,
          body: JSON.stringify(options.body)
        };
      }
    }

    const response = await fetch(
      this.buildUrl(options.url, options.query),
      opts
    );

    // Renew once, then send the same request again. Only if the renewal itself
    // fails does this fall through to responseHandler, which calls
    // onUnauthorized and ends the session — so someone genuinely removed from
    // staff is still signed out, on the retry rather than the first response.
    if (response.status === 401 && !isRetry && this.onTokenExpired) {
      const renewed = await this.onTokenExpired();

      if (renewed) {
        return this.sendRequest(options, true);
      }
    }

    return this.responseHandler(response);
  }

  public setToken(accessToken: string) {
    this._defaultHeaders = {
      ...this._defaultHeaders,
      Authorization: `Bearer ${accessToken}`
    };
  }

  public post(
    url: string,
    body: IBody | FormData,
    options: RequestOptions = {}
  ) {
    return this.sendRequest({
      url,
      method: "POST",
      body,
      ...options
    });
  }

  public get(url: string, options: RequestOptions = {}) {
    return this.sendRequest({
      url,
      method: "GET",
      ...options
    });
  }

  public put(
    url: string,
    body: IBody | FormData,
    options: RequestOptions = {}
  ) {
    return this.sendRequest({
      url,
      method: "PUT",
      body,
      ...options
    });
  }

  public patch(
    url: string,
    body: IBody | FormData,
    options: RequestOptions = {}
  ) {
    return this.sendRequest({
      url,
      method: "PATCH",
      body,
      ...options
    });
  }

  public del(url: string, options: RequestOptions = {}) {
    return this.sendRequest({
      url,
      method: "DELETE",
      ...options
    });
  }

  public upload(path: string, body: FormData, options: XHROptions = {}): IXHR {
    const xhr = new XMLHttpRequest();

    const headers: IHeaders = {
      ...this._defaultHeaders,
      ...options.headers
    };

    const url = this.buildUrl(path, options.query);

    xhr.open("POST", url, true);

    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });

    if (options.onProgress) {
      xhr.upload.onprogress = options.onProgress;
    }

    xhr.onerror = () => {
      if (options.onFail) {
        options.onFail(new APIError("Something went wrong", 500));
      }
    };

    xhr.onabort = () => {
      if (options.onCancel) {
        options.onCancel();
      }
    };

    xhr.onload = function onload() {
      if (this.status >= 200 && this.status < 300 && options.onSuccess) {
        options.onSuccess(JSON.parse(this.response));
      } else if (this.status >= 400 && this.status < 500 && options.onFail) {
        options.onFail(new APIError(JSON.parse(this.response), this.status));
      } else if (options.onFail) {
        options.onFail(new APIError("Something went wrong", 500));
      }
    };

    xhr.send(body);

    return {
      abort: () => xhr.abort()
    };
  }
}
